import React, { useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Alert,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  Divider,
} from '@mui/material';
import {
  CheckCircle,
  Cancel,
  Schedule,
  Person,
  AccessTime,
  Warning,
  Info,
} from '@mui/icons-material';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { updateAppointmentStatus } from '../features/appointments/appointmentsSlice';

const AppointmentStatusManager = ({ 
  appointment, 
  onStatusChange,
  userRole = 'patient' 
}) => {
  const dispatch = useDispatch();
  const [openStatusDialog, setOpenStatusDialog] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState('');
  const [loading, setLoading] = useState(false);
  const [notes, setNotes] = useState('');

  const getStatusColor = (status) => {
    const colors = {
      'Pending': 'warning',
      'Confirmed': 'info',
      'Scheduled': 'primary',
      'Arrived': 'secondary',
      'In Room': 'info',
      'Completed': 'success',
      'Cancelled': 'error',
      'No Show': 'error'
    };
    return colors[status] || 'default';
  };

  const getStatusIcon = (status) => {
    const icons = {
      'Pending': <Schedule />,
      'Confirmed': <CheckCircle />,
      'Scheduled': <Schedule />,
      'Arrived': <Person />,
      'In Room': <Person />,
      'Completed': <CheckCircle />,
      'Cancelled': <Cancel />,
      'No Show': <Warning />
    };
    return icons[status] || <Info />;
  };

  const getAvailableTransitions = (currentStatus, role) => {
    const transitions = {
      'Pending': ['Confirmed', 'Cancelled'],
      'Confirmed': ['Scheduled', 'Cancelled'],
      'Scheduled': ['Arrived', 'Cancelled', 'No Show'],
      'Arrived': ['In Room', 'Cancelled'],
      'In Room': ['Completed', 'Cancelled'],
      'Completed': [],
      'Cancelled': [],
      'No Show': []
    };

    // Role-based restrictions
    if (role === 'patient') {
      // Patients can only cancel or confirm
      return transitions[currentStatus]?.filter(status => 
        ['Cancelled', 'Confirmed'].includes(status)
      ) || [];
    }

    if (role === 'doctor') {
      // Doctors can manage all statuses
      return transitions[currentStatus] || [];
    }

    if (role === 'receptionist') {
      // Receptionists can manage check-in and basic statuses
      return transitions[currentStatus]?.filter(status => 
        ['Confirmed', 'Arrived', 'Cancelled'].includes(status)
      ) || [];
    }

    return transitions[currentStatus] || [];
  };

  const handleStatusChange = async (newStatus) => {
    if (!newStatus) {
      toast.error('Please select a status');
      return;
    }

    setLoading(true);
    try {
      await dispatch(updateAppointmentStatus({
        id: appointment.id,
        status: newStatus,
        notes: notes.trim() || undefined
      })).unwrap();

      toast.success(`Appointment status updated to ${newStatus}`);
      setOpenStatusDialog(false);
      setSelectedStatus('');
      setNotes('');
      onStatusChange?.(newStatus);
    } catch (error) {
      const errorMessage = typeof error === 'string' 
        ? error 
        : error?.message || error?.detail || 'Failed to update appointment status';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const availableTransitions = getAvailableTransitions(appointment.status, userRole);

  if (availableTransitions.length === 0) {
    return (
      <Chip
        icon={getStatusIcon(appointment.status)}
        label={appointment.status}
        color={getStatusColor(appointment.status)}
        variant="outlined"
        size="small"
      />
    );
  }

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Chip
          icon={getStatusIcon(appointment.status)}
          label={appointment.status}
          color={getStatusColor(appointment.status)}
          variant="outlined"
          size="small"
        />
        <Button
          size="small"
          variant="outlined"
          onClick={() => setOpenStatusDialog(true)}
          disabled={availableTransitions.length === 0}
        >
          Update
        </Button>
      </Box>

      <Dialog 
        open={openStatusDialog} 
        onClose={() => setOpenStatusDialog(false)}
        maxWidth="sm" 
        fullWidth
      >
        <DialogTitle>Update Appointment Status</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            Current Status: <strong>{appointment.status}</strong>
          </Alert>

          <Grid container spacing={2} sx={{ mt: 1 }}>
            {availableTransitions.map((status) => (
              <Grid item xs={6} sm={4} key={status}>
                <Card 
                  sx={{ 
                    cursor: 'pointer',
                    border: selectedStatus === status ? 2 : 1,
                    borderColor: selectedStatus === status ? 'primary.main' : 'divider',
                    '&:hover': { borderColor: 'primary.main' }
                  }}
                  onClick={() => setSelectedStatus(status)}
                >
                  <CardContent sx={{ textAlign: 'center', py: 2 }}>
                    <Box sx={{ color: `${getStatusColor(status)}.main`, mb: 1 }}>
                      {getStatusIcon(status)}
                    </Box>
                    <Typography variant="body2" fontWeight="medium">
                      {status}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          <Divider sx={{ my: 2 }} />

          <TextField
            label="Notes (Optional)"
            multiline
            rows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Add any notes about this status change..."
            fullWidth
          />

          {selectedStatus && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>Status Change:</strong> {appointment.status} → {selectedStatus}
                {selectedStatus === 'Cancelled' && ' - This will notify the patient'}
                {selectedStatus === 'Completed' && ' - This will mark the appointment as finished'}
                {selectedStatus === 'No Show' && ' - This will mark the patient as not having attended'}
              </Typography>
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenStatusDialog(false)}>
            Cancel
          </Button>
          <Button 
            onClick={() => handleStatusChange(selectedStatus)}
            variant="contained"
            disabled={!selectedStatus || loading}
            startIcon={loading ? <CircularProgress size={20} /> : null}
          >
            Update Status
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AppointmentStatusManager;